import BackendService from './BackendService';

/**
 * Service to handle tracking model views with debouncing and duplicate prevention
 */
export class RecentlyViewedService {
  private static instance: RecentlyViewedService;
  private backendService: BackendService;
  private pendingRequests: Map<string, NodeJS.Timeout> = new Map();
  private recentlyTracked: Set<string> = new Set();
  private readonly DEBOUNCE_DELAY = 1000; // 1 second debounce
  private readonly DUPLICATE_PREVENTION_WINDOW = 5 * 60 * 1000; // 5 minutes

  constructor(backendService?: BackendService) {
    this.backendService = backendService || new BackendService();
  }

  public static getInstance(): RecentlyViewedService {
    if (!RecentlyViewedService.instance) {
      RecentlyViewedService.instance = new RecentlyViewedService();
    }
    return RecentlyViewedService.instance;
  }

  /**
   * Track a model view with debouncing and duplicate prevention
   */
  public trackModelView(modelType: string, modelId: string): void {
    const key = `${modelType}:${modelId}`;

    // Check if we've recently tracked this model
    if (this.recentlyTracked.has(key)) {
      return;
    }

    // Clear any pending request for this model
    const existingTimeout = this.pendingRequests.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set up debounced request
    const timeout = setTimeout(() => {
      this.sendViewRequest(modelType, modelId, key);
      this.pendingRequests.delete(key);
    }, this.DEBOUNCE_DELAY);

    this.pendingRequests.set(key, timeout);
  }

  /**
   * Send the actual view request to the backend
   */
  private async sendViewRequest(modelType: string, modelId: string, key: string): Promise<void> {
    try {
      await this.backendService.registerModelView(modelType, modelId);

      // Mark as recently tracked to prevent duplicates
      this.recentlyTracked.add(key);

      // Remove from recently tracked after the prevention window
      setTimeout(() => {
        this.recentlyTracked.delete(key);
      }, this.DUPLICATE_PREVENTION_WINDOW);
    } catch (error) {
      // Log error but don't throw - tracking failures shouldn't break user experience
      console.warn('Failed to track model view:', error);
    }
  }

  /**
   * Get recently viewed items with optional filtering and pagination
   */
  public async getRecentlyViewed(modelType?: string, limit?: number, page?: number) {
    try {
      const filters: any = {};
      if (modelType) filters.modelType = modelType;
      if (limit) filters.limit = limit;
      if (page) filters.page = page;

      return await this.backendService.getRecentlyViewed(filters);
    } catch (error) {
      console.error('Failed to fetch recently viewed items:', error);
      throw error;
    }
  }

  /**
   * Clear all pending requests (useful for cleanup)
   */
  public clearPendingRequests(): void {
    this.pendingRequests.forEach((timeout) => clearTimeout(timeout));
    this.pendingRequests.clear();
  }
}

export default RecentlyViewedService;
